import React from "react";
import { Heart, Sparkles, Quote } from "lucide-react";
import SectionNumber from "./SectionNumber";
import Embers from "./Embers";

const ANIMALS = [
  {
    n: "Pónik",
    role: "Mang & barátai",
    fact: "Lassú séta, érintés, simogatás",
    img: "/images/nub9tov3.jpg",
  },
  {
    n: "Alpakák",
    role: "Di & Dra",
    fact: "Kíváncsi, nyugodt, plüss-ölelés",
    img: "/images/zhbsgoo7.jpeg",
  },
  {
    n: "Vizsla",
    role: "A négylábú házigazda",
    fact: "Hűséges kísérő, mosolygós",
    img: "/images/wdrhfyom.jpeg",
  },
];

export default function Animals() {
  return (
    <section
      data-testid="animals-section"
      className="relative bg-pkbeige py-24 sm:py-32 overflow-hidden"
    >
      <SectionNumber n="06" />

      {/* Soft warm blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #FFA64D 0%, #D90429 55%, transparent 75%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full opacity-[0.06] blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, #D90429 0%, #FFA64D 50%, transparent 70%)",
        }}
      />

      <Embers count={10} variant="light" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14 sm:mb-20 items-end">
          <div className="lg:col-span-7 reveal">
            <span className="eyebrow">Családbarát</span>
            <h2 className="font-serif text-pkcharcoal text-4xl sm:text-5xl lg:text-7xl mt-6 leading-[1]">
              Nem csak étel<span className="text-pkred">,</span>
              <br />
              <span className="italic">élmény is.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 reveal">
            <div className="relative pl-8">
              <Quote
                className="absolute -top-1 left-0 w-6 h-6 text-pkred/80"
                strokeWidth={1.2}
                fill="currentColor"
              />
              <p className="font-serif italic text-pkcharcoal/80 text-lg sm:text-xl leading-snug">
                A Pakkaland nem csak helyszín — barátaink, az állatok teszik
                igazán élménnyé az itt töltött időt.
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span className="block w-8 h-px bg-pkred" />
                <span className="text-[0.6rem] tracking-[0.3em] uppercase text-pkcharcoal/55 font-semibold">
                  A család
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Asymmetric animal grid: featured + 2 stacked */}
        <div className="grid lg:grid-cols-12 gap-5 sm:gap-6">
          {/* Featured card (large, left) */}
          <article
            data-testid="animal-card-featured"
            className="lg:col-span-7 group relative aspect-[4/5] sm:aspect-[16/12] lg:aspect-auto lg:min-h-[620px] overflow-hidden bg-pkcharcoal reveal"
          >
            <img
              src={ANIMALS[0].img}
              alt={ANIMALS[0].n}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal via-pkcharcoal/30 to-transparent" />

            {/* Top right "MEET" stamp */}
            <div className="absolute top-6 right-6 flex items-center gap-2 bg-pkred/95 backdrop-blur-sm px-3 py-2">
              <Heart
                className="w-3.5 h-3.5 text-white"
                strokeWidth={1.8}
                fill="currentColor"
              />
              <span className="text-[0.6rem] tracking-[0.35em] uppercase text-white font-bold">
                Ismerd meg
              </span>
            </div>

            {/* Bottom content */}
            <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-10">
              <div className="flex items-end gap-6">
                <div className="font-serif text-pkred/40 text-7xl leading-none font-extralight">
                  01
                </div>
                <div className="flex-1">
                  <div className="text-white/65 text-[0.65rem] tracking-[0.3em] uppercase font-semibold">
                    {ANIMALS[0].role}
                  </div>
                  <h3 className="font-serif text-white text-4xl sm:text-5xl mt-1 leading-tight">
                    {ANIMALS[0].n}
                  </h3>
                  <div className="mt-4 flex items-center gap-2 text-white/75 text-sm">
                    <Sparkles
                      className="w-3.5 h-3.5 text-pkred"
                      strokeWidth={1.6}
                    />
                    {ANIMALS[0].fact}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Right column - 2 stacked smaller cards */}
          <div className="lg:col-span-5 grid grid-cols-1 gap-5 sm:gap-6">
            {ANIMALS.slice(1).map((a, i) => (
              <article
                key={a.n}
                data-testid={`animal-card-${i + 1}`}
                className="group relative aspect-[16/10] lg:aspect-auto lg:min-h-[300px] overflow-hidden bg-pkcharcoal reveal"
                style={{ transitionDelay: `${(i + 1) * 100}ms` }}
              >
                <img
                  src={a.img}
                  alt={a.n}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/95 via-pkcharcoal/35 to-transparent" />

                {/* Number stamp */}
                <div className="absolute top-5 left-5 text-white/30 font-serif text-3xl font-extralight leading-none">
                  0{i + 2}
                </div>

                {/* Bottom content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7">
                  <div className="text-white/65 text-[0.6rem] tracking-[0.3em] uppercase font-semibold">
                    {a.role}
                  </div>
                  <h3 className="font-serif text-white text-2xl sm:text-3xl mt-1 leading-tight">
                    {a.n}
                  </h3>

                  {/* Fact reveal on hover */}
                  <div className="mt-3 flex items-center gap-2 text-white/70 text-xs sm:text-sm overflow-hidden">
                    <span className="block w-6 h-px bg-pkred shrink-0 group-hover:w-12 transition-all duration-500" />
                    <span className="opacity-80 group-hover:opacity-100 transition-opacity">
                      {a.fact}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Bottom strip — fun stats / line */}
        <div className="mt-14 sm:mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-pkash/60 reveal">
          {[
            { n: "5+", l: "Állat lakó" },
            { n: "2", l: "Alpaka barát" },
            { n: "365", l: "Nap szeretet" },
            { n: "∞", l: "Ölelés" },
          ].map((s) => (
            <div
              key={s.l}
              className="bg-pkbeige p-5 sm:p-6 text-center group hover:bg-white transition-colors duration-500"
            >
              <div className="font-serif text-pkred text-3xl sm:text-4xl font-medium leading-none">
                {s.n}
              </div>
              <div className="text-[0.6rem] tracking-[0.25em] uppercase text-pkcharcoal/55 mt-3 font-semibold">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
